import { Injectable } from '@angular/core';
import { BehaviorSubject, find, Observable, of } from 'rxjs';
import { PAX_TYPE, SeatPassangerEstatus, StatusBag } from '@app/enums';
import { Passenger, Reservation, Segment } from '@app/models/micro';
import { SeatPassanger } from '@app/interfaces/passenger.interface';
import { BagsPassangerInt } from '@app/interfaces/micro/bags.interface';
import { PassengerService } from '../micro/passenger.service';
import { ApiAncillaries } from './ancillaries.service';
import {
  CartInt,
  CartItemsInt,
  TypeAncillaries,
  SourceFlowCartType,
} from '@app/interfaces/micro/cart.interface';
import { BagInsuranceOffer } from '@app/interfaces/micro/bag-insurance.interface';
import { seats } from '@app/models/micro/response/respBenefits.model';
import { LoadingService } from '../loading.service';
import { Router } from '@angular/router';
import { GlobalsService } from './globals.service';

// Interfaces específicas para el servicio
interface BagExtraWeight {
  bag: BagsPassangerInt;
  passenger: Passenger;
}

interface CartState {
  bagsTotalAmount: number;
  bagsDetail: any[];
  items: CartItemsInt[];
  seatsTotalAmount: number;
  seatsDetail: any[];
  bagsTotal: number;
  totalAmount: number;
  seatsTotal: number;
}

// Constantes del servicio
const CART_CONSTANTS = {
  DEFAULT_CURRENCY: 'MXN',
  BAGS_TITLE_KEY: 'payment.bagsTitle',
} as const;
@Injectable({
  providedIn: 'root',
})
export class CartService {
  stackBagsToPay: BagsPassangerInt[];
  stackBagsCarryOnExtraWeight: BagsPassangerInt[];
  stackSeatsToPay: SeatPassanger[];
  flowSource: SourceFlowCartType;
  bagExtraWeight;
  selectedTrip: Segment;

  private cart: CartInt = {
    bagsTotalAmount: 0,
    bagsDetail: [],
    items: [],
    seatsTotalAmount: 0,
    seatsDetail: [],
    bagsTotal: 0,
    totalAmount: 0,
    seatsTotal: 0,
  };

  private cart$: BehaviorSubject<CartInt> = new BehaviorSubject<CartInt>(this.cart);
  private currencyCode = 'MXN';

  constructor(
    private _passenger: PassengerService,
    private _ancillaries: ApiAncillaries,
    private _global: GlobalsService,
    private _loadingService: LoadingService,
    private _router: Router
  ) {}

  setCurrency(reservation: Reservation): void {
    const code =
      (reservation as any)?.flightSummary?.currencyCode ??
      (reservation as any)?.currency?.currencyCode;
    this.currencyCode =
      typeof code === 'string' && code.length > 0
        ? code.toUpperCase()
        : CART_CONSTANTS.DEFAULT_CURRENCY;
  }

  overrideCurrency(code: string): void {
    this.currencyCode = code;
  }

  listen() {
    return this.cart$.asObservable();
  }

  private get passengersSelectionBags() {
    return this._passenger.getSelectPassengersToBags();
  }

  private get passengersSelectionsSeats() {
    return this._passenger.getSelectPassengersToSeats();
  }

  private buildBagsToCart() {
    this.cart.bagsDetail = [];
    let bagsTotalAmount = 0;
    let bagsTotal = 0;

    for (const passenger of this.passengersSelectionBags) {
      const bags = passenger.bagStack.filter(
        (bag) => bag.status === StatusBag.TO_PAY && !bag?.used
      );

      if (bags.length > 0) {
        this.cart.bagsDetail.push({ ...passenger, bags });
        bagsTotal += bags.length;

        bagsTotalAmount += bags.reduce((acc, bag) => {
          const price = bag.currency?.total ?? bag.currencyPerLeg?.[0]?.currency?.total ?? 0;
          return acc + price;
        }, 0);
      }
    }
    // Actualizar totales del carrito
    this.cart.bagsTotal = bagsTotal;
    this.cart.bagsTotalAmount = bagsTotalAmount;
    let bagItem: CartItemsInt;
    bagItem = {
      title: 'payment.bagsTitle',
      quantity: this.cart.bagsTotal,
      subtotal: this.cart.bagsTotalAmount,
      currencyCode: this.currencyCode,
      detail: this.cart.bagsDetail,
      typeAncillarie: 'bags',
    };

    this.cart.items = this.cart.items.filter((item) => item.typeAncillarie == 'bags');
    if (bagsTotal > 0) {
      this.cart.items.push(bagItem);
    }
    // Actualizar monto total del carrito
    this.cart.totalAmount = this.cart.bagsTotalAmount; // + this.cart.seatsTotalAmount;
  }

  private buildBagsCarryonToCart() {
    const bagsData = { total: 0, amount: 0, details: [] };
    const carryOnData = { total: 0, amount: 0, details: [] };

    for (const passenger of this.passengersSelectionBags) {
      const bags = passenger.bagStack.filter(
        (bag) => bag.status === StatusBag.TO_PAY && !bag?.used
      );

      const carryOnBags = [
        ...(passenger.bagStackCarryon?.filter(
          (bag) => bag.status === StatusBag.TO_PAY && !bag?.used
        ) || []),
        ...(passenger.bagOfferCarryOnStack?.filter((bag) => bag.status === StatusBag.TO_PAY) || []),
      ];

      this.processBagGroup(bags, passenger, bagsData);
      this.processBagGroup(carryOnBags, passenger, carryOnData);
    }

    // Actualizar totales del carrito
    this.cart.bagsTotal = bagsData.total + carryOnData.total;
    this.cart.bagsTotalAmount = bagsData.amount + carryOnData.amount;

    // Filtrar items existentes de bags y carryOn
    this.cart.items = this.cart.items.filter(
      (item) => item.typeAncillarie !== 'bags' && item.typeAncillarie !== 'carryOn'
    );

    // Agregar items al carrito si existen
    if (bagsData.total > 0) {
      this.cart.items.push({
        title: 'payment.bagsTitle',
        quantity: bagsData.total,
        subtotal: bagsData.amount,
        currencyCode: this.currencyCode,
        detail: bagsData.details,
        typeAncillarie: 'bags',
      });
    }

    if (carryOnData.total > 0) {
      this.cart.items.push({
        title: 'Equipaje de mano',
        quantity: carryOnData.total,
        subtotal: carryOnData.amount,
        currencyCode: this.currencyCode,
        detail: carryOnData.details,
        typeAncillarie: 'carryOn',
      });
    }

    this.cart.totalAmount = this.cart.bagsTotalAmount;
  }

  private processBagGroup(
    bags: BagsPassangerInt[],
    passenger: Passenger,
    data: { total: number; amount: number; details: any[] }
  ) {
    if (bags.length > 0) {
      data.details.push({ ...passenger, bags });
      data.total += bags.length;
      data.amount += bags.reduce((acc, bag) => acc + this.getBagPrice(bag), 0);
    }
  }

  private getBagPrice(bag: BagsPassangerInt): number {
    return bag.currency?.total ?? bag.currencyPerLeg?.[0]?.currency?.total ?? 0;
  }

  private buildSeatsToCart() {
    this.cart.seatsDetail = [];
    let seatsTotalAmount = 0;
    let seatsTotal = 0;

    for (const passenger of this.passengersSelectionsSeats) {
      const seats = passenger.seatsNew.filter(
        (seat) => seat.status === SeatPassangerEstatus.PENDING_PAYMENT && seat?.currency?.total > 0
      );

      if (seats.length > 0) {
        this.cart.seatsDetail.push({ ...passenger, seatsNew: seats });
        seatsTotal += seats.length;

        seatsTotalAmount += seats.reduce((acc, seat) => {
          const price = seat.currency?.total ?? 0;
          return acc + price;
        }, 0);
      }
    }

    // Actualizar totales del carrito
    this.cart.seatsTotal = seatsTotal;
    this.cart.seatsTotalAmount = seatsTotalAmount;
    let seatItem: CartItemsInt;

    if (this.getStackSeatsToPay()?.length > 0) {
      // Crear o actualizar item de asiento en el carrito
      seatItem = {
        title: 'payment.seat',
        quantity: this.cart.seatsTotal,
        subtotal: this.cart.seatsTotalAmount,
        currencyCode: this.currencyCode,
        detail: this.cart.seatsDetail,
        typeAncillarie: 'seats',
      };
    }

    this.cart.items = this.cart.items.filter((item) => item.typeAncillarie == 'seats');
    if (seatsTotal > 0) {
      this.cart.items.push(seatItem);
    }
    // Actualizar monto total del carrito
    this.cart.totalAmount = this.cart.bagsTotalAmount + this.cart.seatsTotalAmount;
  }

  setBagExtraWeight(bag: BagsPassangerInt, passenger: Passenger) {
    this.bagExtraWeight = { bag, passenger };
  }

  getBagExtraWeight() {
    return this.bagExtraWeight;
  }

  /** Almacena el total y moneda del upgrade para construir el carrito */
  private upgradeTotal = 0;
  private upgradeCurrency = 'MXN';
  private upgradeSelectedSegments: any[] = [];
  private upgradeOffers: Map<string, any> = new Map();

  setUpgradeCart(
    total: number,
    currencyCode: string,
    selectedSegments?: any[],
    upgradeOffers?: Map<string, any>
  ): void {
    this.upgradeTotal = total;
    this.upgradeCurrency = currencyCode;
    if (selectedSegments) {
      this.upgradeSelectedSegments = selectedSegments;
    }
    if (upgradeOffers) {
      this.upgradeOffers = upgradeOffers;
    }
    this.updateCart();
  }

  private buildUpgradeToCart(): void {
    if (this.upgradeTotal > 0) {
      const reservation = this._global.getReservation();

      // Si hay múltiples segmentos, crear un item por cada segmento
      if (this.upgradeSelectedSegments.length > 1 && reservation?.passengers) {
        this.upgradeSelectedSegments.forEach((selectedSegment) => {
          const detail: any[] = [];
          const segmentCode = selectedSegment.segmentCode;
          let route = '';

          // Obtener la ruta del segmento
          const segment = reservation.segments?.find((seg) => seg.segmentCode === segmentCode);
          if (segment) {
            route = `${segment.departureAirport} - ${segment.arrivalAirport}`;
          }

          // Obtener la oferta de upgrade para este segmento
          const offerData = this.upgradeOffers.get(segmentCode);
          const currency = offerData?.currency;

          // Calcular el subtotal para este segmento (costo por pasajero * número de pasajeros)
          // CVT20-14292: los infantes no ocupan asiento, no se les cobra el upgrade.
          const passengers = reservation.passengers.filter(
            (p) => (p.checkinSelected || p.isSelectedToCheckin) && p.paxType !== PAX_TYPE.INFANT
          );
          const costPerPassenger = currency?.total || 0;
          const segmentSubtotal = costPerPassenger * passengers.length;

          // Agregar cada pasajero al detalle con su costo individual
          passengers.forEach((passenger) => {
            detail.push({
              passengerName: passenger.displayName,
              passengerNameNumber: passenger.nameRefNumber,
              cost: costPerPassenger,
              currencyCode: currency?.currencyCode || this.upgradeCurrency,
              segmentCode: segmentCode,
              route: route,
            });
          });

          // Crear un item del carrito para este segmento
          const upgradeItem: CartItemsInt = {
            title: 'payment.upgrade_premier',
            quantity: detail.length,
            subtotal: segmentSubtotal,
            currencyCode: this.upgradeCurrency,
            detail: detail,
            typeAncillarie: 'upgrade',
          };
          this.cart.items.push(upgradeItem);
        });

        this.cart.totalAmount = this.upgradeTotal;
      } else if (this.upgradeSelectedSegments.length === 1 && reservation?.passengers) {
        // Un solo segmento - comportamiento actual
        const detail: any[] = [];
        const selectedSegment = this.upgradeSelectedSegments[0];
        const segmentCode = selectedSegment.segmentCode;
        let route = '';

        // Obtener la ruta del segmento
        const segment = reservation.segments?.find((seg) => seg.segmentCode === segmentCode);
        if (segment) {
          route = `${segment.departureAirport} - ${segment.arrivalAirport}`;
        }

        // Obtener la oferta de upgrade para este segmento
        const offerData = this.upgradeOffers.get(segmentCode);
        const currency = offerData?.currency;
        const costPerPassenger = currency?.total || 0;

        // Agregar cada pasajero al detalle con su costo individual
        // CVT20-14292: los infantes no ocupan asiento, no se les cobra el upgrade.
        const passengers = reservation.passengers.filter(
          (p) => (p.checkinSelected || p.isSelectedToCheckin) && p.paxType !== PAX_TYPE.INFANT
        );

        passengers.forEach((passenger) => {
          detail.push({
            passengerName: passenger.displayName,
            passengerNameNumber: passenger.nameRefNumber,
            cost: costPerPassenger,
            currencyCode: currency?.currencyCode || this.upgradeCurrency,
            segmentCode: segmentCode,
            route: route,
          });
        });

        const upgradeItem: CartItemsInt = {
          title: 'payment.upgrade_premier',
          quantity: detail.length || 1,
          subtotal: this.upgradeTotal,
          currencyCode: this.upgradeCurrency,
          detail: detail,
          typeAncillarie: 'upgrade',
        };
        this.cart.items.push(upgradeItem);
        this.cart.totalAmount = this.upgradeTotal;
      }
    }
  }

  removeBagsToPayCart() {
    this.passengersSelectionBags.map((x) => {
      x.bagStack = x.bagStack.filter((y) => y.status != StatusBag.TO_PAY);
    });
    return this.getCart();
  }

  removeStackBagsToPayCarryOn() {
    /*for (const passenger of this.passengersSelectionBags) {
      for (const bag of passenger.bagStackCarryon) {
        if (bag.status === StatusBag.TO_PAY) {
          bag.status = StatusBag.INCLUDE;
        }
      }
    }
    return this.getCart();*/
    this.passengersSelectionBags.map((x) => {
      x.bagStack = x.bagStack.filter((y) => y.status != StatusBag.TO_PAY);
      x.bagStackCarryon = x.bagStackCarryon.filter((y) => y.status != StatusBag.TO_PAY);
    });
    return this.getCart();
  }

  removeStackBagsCarryOnExtraWeight() {
    for (const passenger of this.passengersSelectionBags) {
      passenger.bagStackCarryon = passenger.bagStackCarryon.filter((bag) => bag.isExtraWeight);
    }
  }

  removeStackBagsExtraWeight() {
    this.passengersSelectionBags.map((x) => {
      x.bagStack.map((bag) => {
        if (bag.isExtraWeight) {
          bag.isExtraWeight = false;
          bag.extraWeight = 0;
          bag.weightDifference = 0;

          if (bag.code) {
            bag.rficSubcode = bag.code;
          } else {
            bag.rficSubcode = '08K';
          }
        }
      });
    });
    return this.getCart();
  }

  removeSeatsToCart(resetSeats = null) {
    const reserva = this._global.getReservation();
    const {
      flightSummary: { currentSegment },
    } = reserva;
    const passengers = this._passenger.getPassengerSelection();
    //Se quitan los asientos a nivel de memoria para limpiar el carrito
    for (const passenger of passengers) {
      if (resetSeats?.length > 0) {
        const findSeat = resetSeats.find((option) => option.nameNumber == passenger.nameRefNumber);

        if (findSeat) {
          const { seats } = findSeat;
          const seat = seats.find((s) => s.segmentCode == currentSegment.segmentCode);
          const seatPax = passenger.seats.find((s) => s.segmentCode == currentSegment.segmentCode);
          if (seat) {
            Object.assign(seatPax, seat);
            seatPax.currency = null;
            seatPax.cost = null;
            seatPax.status = SeatPassangerEstatus.ASSIGNED;
          }
        }
      }

      passenger.seatsNew = [];
      this._global.setPassenger(passenger);
    }
  }

  cleanBagExtraWeight() {
    this.bagExtraWeight.bag.weightDifference = 0;
    this.bagExtraWeight.bag.extraWeight = 0;
  }

  getCart(): CartInt {
    const insuranceBackup = {
      bagInsuranceTotal: this.cart?.bagInsuranceTotal,
      bagInsuranceTotalAmount: this.cart?.bagInsuranceTotalAmount,
      bagInsuranceDetail: this.cart?.bagInsuranceDetail,
      // Preservar las líneas ya desglosadas por tipo de seguro (viaje, equipaje,
      // cancelación) para no colapsarlas al reconstruir el carrito.
      items: this.cart?.items?.filter((item) => item.typeAncillarie === 'bagInsurance') ?? [],
    };

    this.cart = {
      bagsTotalAmount: 0,
      bagsDetail: [],
      items: [],
      seatsTotalAmount: 0,
      seatsDetail: [],
      bagsTotal: 0,
      totalAmount: 0,
      seatsTotal: 0,
    };

    switch (this.flowSource) {
      case SourceFlowCartType.BAGS:
        this.buildBagsToCart();
        break;
      case SourceFlowCartType.CARRY_ON:
        this.buildBagsCarryonToCart();
        break;
      case SourceFlowCartType.SEATS:
        this.buildSeatsToCart();
        break;
      case SourceFlowCartType.EXTRA_WEIGHT:
        this.buildCartExtraWeight();
        break;
      case SourceFlowCartType.MANAGE_EXTRAS:
        // Items ya construidos por addBagInsurance — solo restaurar insurance backup
        break;
      case SourceFlowCartType.UPGRADE:
        this.buildUpgradeToCart();
        break;
    }

    // CVT20-14357: el seguro de equipaje solo aplica a los flujos que lo venden — sin este
    // guard, el backup sobrevivia indefinidamente y se re-agregaba (con su monto)
    // en cualquier flujo posterior (asientos, upgrade, etc.) dentro de la misma sesion.
    // MANAGE_EXTRAS entra aqui porque arma su carrito antes de navegar a pago: si no
    // se restaura, paymentSelect lo reconstruye vacio y arma la terminal con monto cero.
    const insuranceAppliesToFlow =
      this.flowSource === SourceFlowCartType.BAGS ||
      this.flowSource === SourceFlowCartType.MANAGE_EXTRAS;
    if (
      insuranceAppliesToFlow &&
      insuranceBackup.bagInsuranceTotal &&
      insuranceBackup.bagInsuranceTotal > 0
    ) {
      this.cart.bagInsuranceTotal = insuranceBackup.bagInsuranceTotal;
      this.cart.bagInsuranceTotalAmount = insuranceBackup.bagInsuranceTotalAmount;
      this.cart.bagInsuranceDetail = insuranceBackup.bagInsuranceDetail;
      this.cart.totalAmount += insuranceBackup.bagInsuranceTotalAmount ?? 0;

      if (!this.cart.items.some((item) => item.typeAncillarie === 'bagInsurance')) {
        if (insuranceBackup.items.length > 0) {
          // Restaurar las líneas desglosadas por tipo de seguro tal cual se armaron.
          this.cart.items.push(...insuranceBackup.items);
        } else {
          // Fallback (flujo bag insurance clásico): una sola línea agregada.
          this.cart.items.push({
            title: 'manageExtras.cart.insuranceTitle',
            quantity: insuranceBackup.bagInsuranceTotal,
            subtotal: insuranceBackup.bagInsuranceTotalAmount ?? 0,
            currencyCode:
              insuranceBackup.bagInsuranceDetail?.[0]?.currencyCode ?? this.currencyCode,
            detail:
              insuranceBackup.bagInsuranceDetail?.map((d) => ({
                displayName: d.displayName ?? d.nameRefNumber,
                amount: d.amount,
                currencyCode: d.currencyCode,
              })) ?? [],
            typeAncillarie: 'bagInsurance',
          });
        }
      }
    }

    return this.cart;
  }

  /** Retorna el estado actual del carrito sin reconstruirlo */
  getCurrentCart(): CartInt {
    return this.cart;
  }

  updateCart() {
    this.cart$.next(this.getCart());
  }

  buildDelete(reservation: Reservation, typeAncillarie: TypeAncillaries): Observable<any> {
    let observable: Observable<any> = of(null);

    switch (typeAncillarie) {
      case 'bags':
        observable = this._ancillaries.removeAncillaries(reservation);
        break;

      case 'carryOn':
        observable = this._ancillaries.removeAncillaries(reservation);
        break;

      case 'seats':
        // Próximamente: eliminar asientos
        observable = this._ancillaries.removeSeat(reservation);
        break;

      case 'extraWeight':
        // Próximamente: eliminar peso adicional
        break;
    }

    return observable;
  }

  private buildCartExtraWeight() {
    let bookedCartInts = [];

    this.cart.bagsDetail = [];
    let bagsTotalAmount = 0;
    let bagsTotal = 0;
    if (this.getBagExtraWeight) {
      //this.cart.bagsDetail.push({ ...this.getBagExtraWeight().passenger, bags: [this.getBagExtraWeight().bag] });

      for (const passenger of this.passengersSelectionBags) {
        const bags = passenger.bagStack.filter((bag) => bag.isExtraWeight === true);

        if (bags.length > 0) {
          this.cart.bagsDetail.push({ bags });
          bagsTotal = bags.length;
          bagsTotalAmount += bags[0]?.currency.total;
        }
      }

      // Actualizar totales del carrito
      this.cart.bagsTotal = bagsTotal;
      this.cart.bagsTotalAmount = bagsTotalAmount;

      // Crear o actualizar item de maletas en el carrito
      const bagItem: CartItemsInt = {
        title: 'payment.bagsWeight',
        quantity: this.cart.bagsTotal,
        subtotal: this.cart.bagsTotalAmount,
        currencyCode: this.currencyCode,
        detail: this.cart.bagsDetail,
        typeAncillarie: 'extraWeight',
      };

      this.cart.items = this.cart.items.filter((item) => item.typeAncillarie !== 'extraWeight');
      if (bagsTotal > 0) {
        this.cart.items.push(bagItem);
      }
      // Actualizar monto total del carrito
      this.cart.totalAmount += this.cart.bagsTotalAmount + this.cart.seatsTotalAmount;
    }
  }

  private getStackSeatsToPay() {
    this.stackSeatsToPay = [];

    this._passenger.getPassengerSelection().map((x) => {
      this.stackSeatsToPay.push(
        ...x.seatsNew.filter((y) => y.status == SeatPassangerEstatus.PENDING_PAYMENT)
      );
    });

    return this.stackSeatsToPay;
  }

  private getStackSeatsToOld() {
    let stackSeatsToOld = [];
    this._passenger.getPassengerSelection().map((x) => {
      stackSeatsToOld.push(seats);
    });
    return stackSeatsToOld;
  }

  addBagInsurance(
    passengerRefs: string[],
    offer: BagInsuranceOffer,
    passengers: Passenger[]
  ): void {
    const previousAmount = this.cart.bagInsuranceTotalAmount ?? 0;
    this.cart.items = this.cart.items.filter((item) => item.typeAncillarie !== 'bagInsurance');
    this.cart.totalAmount -= previousAmount;

    const total = passengerRefs.length * offer.pricePerPassenger;

    this.cart.bagInsuranceTotal = passengerRefs.length;
    this.cart.bagInsuranceTotalAmount = total;
    this.cart.bagInsuranceDetail = passengerRefs.map((ref) => {
      const pax = passengers.find((p) => p.nameRefNumber === ref);
      const name = pax?.displayName ?? (pax?.firstName ? `${pax.firstName} ${pax.lastName}` : ref);
      return {
        nameRefNumber: ref,
        displayName: name,
        amount: offer.pricePerPassenger,
        currencyCode: offer.currencyCode,
      };
    });

    if (passengerRefs.length > 0) {
      this.cart.items.push({
        title: 'manageExtras.cart.insuranceTitle',
        quantity: passengerRefs.length,
        subtotal: total,
        currencyCode: offer.currencyCode,
        detail: passengerRefs.map((ref) => {
          const pax = passengers.find((p) => p.nameRefNumber === ref);
          const name = pax?.displayName ?? (pax?.firstName ? `${pax.firstName} ${pax.lastName}` : ref);
          return {
            displayName: name,
            amount: offer.pricePerPassenger,
            currencyCode: offer.currencyCode,
          };
        }),
        typeAncillarie: 'bagInsurance',
      });
    }

    this.cart.totalAmount += total;
    this.cart$.next(this.cart);
  }

  removeBagInsurance(): void {
    const previousAmount = this.cart.bagInsuranceTotalAmount ?? 0;
    this.cart.items = this.cart.items.filter((item) => item.typeAncillarie !== 'bagInsurance');
    this.cart.totalAmount -= previousAmount;
    this.cart.bagInsuranceTotal = 0;
    this.cart.bagInsuranceTotalAmount = 0;
    this.cart.bagInsuranceDetail = [];
    this.cart$.next(this.cart);
  }
}
